import {
  BodyBox,
  Button,
  Field,
  Icon,
  MultiSelect,
  Notice,
  pxToRem,
  Text,
  Tooltip,
} from '@lbcamden/lib-react';
import { FunctionComponent, useEffect, useState } from 'react';

import { useRoleInformation } from '~/client/hooks/storeHooks';
import { api } from '~/client/lib/trpc';
import { actionTypeOptions } from '~/common/config/pcfra/recommendations';

import { SectionContainer } from '../../common/SectionContainer';
import { downloadRecommendedActionsReportCsv } from './helpers';
import {
  ColumnContainer,
  RowContainer,
  Actions,
  LabelContainer,
} from './ManagementToolsPage.styled';

export const ManagementToolsPage: FunctionComponent = () => {
  const { isAdmin, isPcfra } = useRoleInformation();

  const [hasRebuiltActivityReport, setHasRebuiltActivityReport] = useState(false);
  const [pcfraActionTypes, setPcfraActionTypes] = useState<string[]>([]);
  const [hasEmptyRecommendedActionReport, setHasEmptyRecommendedActionReport] = useState(false);

  const rebuildActivityReport = api.rebuildActivityReport.useMutation();
  const generateRecommendedActionsReport = api.generateRecommendedActionsReport.useMutation();

  const onClickRebuildActivityReport = async () => {
    await rebuildActivityReport.mutateAsync();
    setHasRebuiltActivityReport(true);
  };

  const onClickGenerateRecommendedActionsReport = async () => {
    const report = await generateRecommendedActionsReport.mutateAsync({
      actionTypes: pcfraActionTypes,
    });

    if (report.length) {
      downloadRecommendedActionsReportCsv(report);
    } else {
      setHasEmptyRecommendedActionReport(true);
    }
  };

  useEffect(() => {
    setHasEmptyRecommendedActionReport(false);
  }, [pcfraActionTypes]);

  return (
    <BodyBox>
      <SectionContainer title="Management tools">
        <Actions>
          {isAdmin && (
            <ColumnContainer>
              <Text variant="h4">Rebuild activity report</Text>

              <RowContainer>
                <Button
                  loading={rebuildActivityReport.isPending}
                  disabled={hasRebuiltActivityReport}
                  onClick={onClickRebuildActivityReport}
                >
                  Rebuild report data
                </Button>
                {hasRebuiltActivityReport && (
                  <Icon $color="dartmouthGreen" height={18} icon="ui/tick" />
                )}
              </RowContainer>
            </ColumnContainer>
          )}

          {isPcfra && (
            <ColumnContainer>
              <Text variant="h4">Generate PCFRA recommended actions report</Text>
              <Field
                css={{ maxWidth: pxToRem(450) }}
                label={
                  <LabelContainer>
                    Action types <Tooltip content="All types are returned if none are selected" />
                  </LabelContainer>
                }
              >
                <MultiSelect
                  options={actionTypeOptions}
                  value={pcfraActionTypes}
                  onChange={setPcfraActionTypes}
                />
              </Field>
              <Button
                loading={generateRecommendedActionsReport.isPending}
                onClick={onClickGenerateRecommendedActionsReport}
              >
                Generate
              </Button>

              {hasEmptyRecommendedActionReport && (
                <Notice
                  infoWithIcon
                  message="No recommended actions found for the selected action types."
                />
              )}
            </ColumnContainer>
          )}
        </Actions>
      </SectionContainer>
    </BodyBox>
  );
};
